import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import ModeToggle from '../components/ModeToggle';
import DailyQuestionsButton from '../components/DailyQuestionsButton';
import TestDayCountdown from '../components/TestDayCountdown';
import { useApp } from '../context/AppContext';
import { Scroll, Wand2, Crown, Castle } from 'lucide-react';

const KINGS = [
  { key: 'math', name: 'King Numerus', subject: 'Mathematics', url: 'https://customer-assets-jt897jd0.emergentagent.net/job_63727204-8e2f-45b7-8117-c2998f36774d/artifacts/82492prj_ChatGPT%20Image%20Jun%2014%2C%202026%2C%2003_17_14%20PM.png', color: '#8C1C13' },
  { key: 'english', name: 'King Lexicon', subject: 'English & Reading', url: 'https://customer-assets-jt897jd0.emergentagent.net/job_63727204-8e2f-45b7-8117-c2998f36774d/artifacts/m00sax2i_ChatGPT%20Image%20Jun%2014%2C%202026%2C%2003_39_08%20PM.png', color: '#B08D57' },
  { key: 'science', name: 'King Alchemus', subject: 'Science', url: 'https://customer-assets-jt897jd0.emergentagent.net/job_63727204-8e2f-45b7-8117-c2998f36774d/artifacts/w9vdbhda_ChatGPT%20Image%20Jun%2014%2C%202026%2C%2005_14_48%20PM.png', color: '#5B4A8B' },
];

// If the local generated castle exists at /assets/castle-bg.png, use it; otherwise fallback to storybook_background from design.
const CASTLE_BG = '/assets/castle-bg.png';
const CASTLE_FALLBACK = 'https://images.unsplash.com/photo-1694086130237-288936c29495?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxhZXJpYWwlMjB2aWV3JTIwY2FzdGxlfGVufDB8fHx8MTc4NTAxOTM0NXww&ixlib=rb-4.1.0&q=85';

export default function StoryBookHome() {
  const { user, logout } = useApp();
  const nav = useNavigate();

  return (
    <div className="min-h-screen relative overflow-x-hidden">
      {/* NAV */}
      <nav className="relative z-30 flex items-center justify-between px-4 sm:px-8 lg:px-16 py-5 flex-wrap gap-3">
        <div data-testid="story-nav-logo" className="font-storybook text-2xl sm:text-3xl font-bold text-[#2C2621]">
          StoryBook <span className="text-[#8C1C13]">Land</span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="hidden lg:flex items-center gap-3">
            {[
              ['Daily Quest', '/daily'],
              ['Score Oracle', '/predictor'],
              ['5-Week Journey', '/study-plan'],
              ['Question Bank', '/bank'],
            ].map(([label, path]) => (
              <button
                key={path}
                data-testid={`story-nav-${label.toLowerCase().replace(/ /g, '-')}`}
                onClick={() => nav(path)}
                className="font-storybook text-sm text-[#2C2621] hover:text-[#8C1C13] transition-colors"
              >
                {label}
              </button>
            ))}
          </div>
          <ModeToggle />
          {user ? (
            <button data-testid="story-nav-logout" onClick={logout} className="font-storybook text-sm text-[#68615A]">Depart</button>
          ) : (
            <button data-testid="story-nav-signin" onClick={() => nav('/auth')} className="storybook-btn px-4 py-1.5 rounded-full text-xs">Enter Realm</button>
          )}
        </div>
      </nav>

      {/* HERO */}
      <section className="relative z-10 px-8 lg:px-16 pt-8 pb-20 max-w-6xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#E8DCC4] border border-[#C9B99A] rounded-full text-sm font-body-story text-[#68615A]">
            <Wand2 size={14} /> A whimsical retelling of the ACT
          </div>
          <h1 className="mt-6 font-storybook text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#2C2621]">
            Three Kings.<br/>One Kingdom.<br/>
            <span className="italic text-[#8C1C13]">Your Crown Awaits.</span>
          </h1>
          <p className="mt-6 text-lg text-[#4A413A] font-body-story max-w-2xl leading-relaxed">
            Journey through the realm alongside Pixie, our fairy tutor. Study with the wise Kings of Math,
            English, and Science. Every answer earns favor at court.
          </p>

          <div className="mt-10 max-w-lg space-y-4">
            <TestDayCountdown variant="storybook" />
            <DailyQuestionsButton variant="storybook" />
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button data-testid="story-cta-towers" onClick={() => nav('/towers')} className="storybook-btn px-5 py-3 rounded-xl text-sm inline-flex items-center gap-2">
              <Castle size={16} /> Climb the Towers of Mastery
            </button>
            <button data-testid="story-cta-predictor" onClick={() => nav('/predictor')} className="storybook-btn px-5 py-3 rounded-xl text-sm inline-flex items-center gap-2">
              <Scroll size={16} /> Consult the Score Oracle
            </button>
            <button data-testid="story-cta-studyplan" onClick={() => nav('/study-plan')} className="storybook-btn px-5 py-3 rounded-xl text-sm inline-flex items-center gap-2">
              <Crown size={16} /> Chart your 5-Week Journey
            </button>
          </div>
        </motion.div>
      </section>

      {/* THE KINGS */}
      <section className="relative z-10 px-8 lg:px-16 py-16 max-w-6xl mx-auto">
        <div className="text-center mb-14">
          <div className="font-body-story italic text-[#68615A]">Meet Your Royal Tutors</div>
          <h2 className="font-storybook text-4xl lg:text-5xl text-[#2C2621] mt-2">The Three Kings of the Realm</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {KINGS.map((k, i) => (
            <motion.div
              key={k.key}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: i * 0.12 }}
              data-testid={`king-card-${k.key}`}
              className="parchment rounded-3xl p-6 text-center"
            >
              <div className="w-full h-64 flex items-end justify-center overflow-hidden">
                <motion.img
                  src={k.url}
                  alt={k.name}
                  className="max-h-full object-contain"
                  animate={{ y: [0, -14, 0], rotate: [0, i % 2 === 0 ? 1.5 : -1.5, 0] }}
                  transition={{
                    duration: 4 + i * 0.4,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: i * 0.35,
                  }}
                  style={{ filter: 'drop-shadow(0 12px 18px rgba(80,50,20,0.25))' }}
                />
              </div>
              <div className="mt-2 font-storybook text-2xl text-[#2C2621]">{k.name}</div>
              <div className="mt-1 font-body-story italic text-[#68615A]">{k.subject}</div>
            </motion.div>
          ))}
        </div>
      </section>

      <footer className="relative z-10 px-8 lg:px-16 py-8 border-t border-[#C9B99A] text-center font-body-story italic text-[#68615A]">
        By royal decree of StoryBook Land · A tale of PrepPulse
      </footer>
    </div>
  );
}
